import React, { useState, useMemo } from 'react';
import { fmtNumber } from '../../shared/format';
import {
  sortRows, downloadCsv, downloadPdf, makeExport,
  SortableHeader, PaginationFooter, TableSkeleton, SectionInsightCard,
} from '../shared';
import ThinScroll from '../../shared/ThinScroll';
import { ErrorState, EmptyState } from '../../shared/StateMessage';
import useTablePage from '../../../hooks/useTablePage';
import { expandableRowProps } from '../../shared/expandableRow';
// Classification patterns live in an editable data file (spec §13) so the referrer→
// category mapping can be maintained in one place, separate from this component's logic.
import REFERRAL_CATEGORIES from '../../../config/ga4ReferralCategories.json';

const REFERRAL_CATEGORY_COLORS = {
  'OTA / Directory': '#32B1FF',
  'Travel / Tourism': '#0ea5e9',
  'Social Media': '#a855f7',
  'Search Engine': '#10b981',
  'Email / Newsletter': '#f59e0b',
  'Maps / Local': '#ef4444',
  'Review / Forum': '#ec4899',
  'AI Assistant': '#14b8a6',
  'Other': '#6b7280',
};

// Three pattern shapes, matched precisely so short patterns don't over-match:
//  - Hostname ("t.co", "booking.com"): whole host or subdomain only — never a mid-string
//    substring, else "t.co" would spuriously match "thedyrt.com".
//  - Path ("google.com/maps"): plain substring, which is what it's meant for.
//  - Word-stem ("tourism", "visit"): matched at a host-label boundary (start of host or
//    just after a dot), so "visit" catches "visitphoenix.com" / "visitarizona.com" but not
//    "revisit.com" or "myvisitor.org".
const matchesReferralPattern = (source, pattern) => {
  if (pattern.includes('/')) return source.includes(pattern);
  if (pattern.includes('.')) return source === pattern || source.endsWith('.' + pattern);
  return source === pattern || source.startsWith(pattern) || source.includes('.' + pattern);
};

export const classifyReferral = (source) => {
  if (!source) return 'Other';
  const normalizedSource = source.toLowerCase();
  for (const { category, patterns } of REFERRAL_CATEGORIES) {
    if (patterns.some((pattern) => matchesReferralPattern(normalizedSource, pattern))) return category;
  }
  return 'Other';
};

/* ============================= All Referrers table ============================= */
const ReferralsTable = ({ data, loading, error }) => {
  const [sort, setSort] = useState({ key: 'sessions', dir: 'desc' });
  const perPage = 10;

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const sorted = useMemo(() => sortRows(data || [], sort.key, sort.dir), [data, sort]);
  const totalItems = sorted.length;
  const { page, setPage, totalPages, sliceStart, sliceEnd } = useTablePage(totalItems, perPage, [data, sort]);
  const paged = sorted.slice(sliceStart, sliceEnd);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No referral data for this period." padding="py-12" />;
  }

  return (
    <div>
      <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">{totalItems} referral{totalItems !== 1 ? 's' : ''}</div>
      <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
        <table className="responsive-card-table w-full text-sm table-fixed min-w-[720px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader label="Source" sortKey="source" currentSort={sort} onSort={handleSort} className="w-[20%]" />
              <SortableHeader label="Referrer URL" sortKey="referrer_url" currentSort={sort} onSort={handleSort} className="w-[30%]" />
              <SortableHeader label="Sessions" sortKey="sessions" currentSort={sort} onSort={handleSort} className="w-[17%]" />
              <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} className="w-[17%]" />
              <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} className="w-[16%]" />
            </tr>
          </thead>
          <tbody>
            {paged.map((row, index) => (
              <tr key={`${row.source}-${row.referrer_url}-${index}`} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Source" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 font-medium whitespace-nowrap truncate max-w-0 max-[499px]:max-w-none" title={row.source || ''}>{row.source || '—'}</td>
                <td data-label="Referrer URL" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 truncate max-w-0 max-[499px]:max-w-none max-[499px]:whitespace-normal max-[499px]:break-all" title={row.referrer_url || ''}>
                  {row.referrer_url || '—'}
                </td>
                <td data-label="Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.sessions)}</td>
                <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.users)}</td>
                <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.conversions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ThinScroll>
      <PaginationFooter pagination={{ total_items: totalItems, total_pages: totalPages }} page={page} perPage={perPage} onPageChange={setPage} label="referrals" />
    </div>
  );
};

/* ============================= Referrals by Category ============================= */
const ReferralsByCategoryTable = ({ data, loading, error }) => {
  const [sort, setSort] = useState({ key: 'sessions', dir: 'desc' });
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [detailPage, setDetailPage] = useState({});
  const detailPerPage = 15;

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const { categories, categoryDetails } = useMemo(() => {
    if (!data || data.length === 0) return { categories: [], categoryDetails: {} };
    const grouped = {};
    const details = {};
    for (const row of data) {
      const cat = classifyReferral(row.source);
      if (!grouped[cat]) { grouped[cat] = { category: cat, sessions: 0, users: 0, conversions: 0, count: 0 }; details[cat] = []; }
      grouped[cat].sessions += row.sessions || 0;
      grouped[cat].users += row.users || 0;
      grouped[cat].conversions += row.conversions || 0;
      grouped[cat].count += 1;
      details[cat].push(row);
    }
    return { categories: Object.values(grouped), categoryDetails: details };
  }, [data]);

  const totalSessions = useMemo(() => categories.reduce((sum, category) => sum + category.sessions, 0), [categories]);

  const enriched = useMemo(() =>
    categories.map((category) => ({
      ...category,
      pct_of_total: totalSessions > 0 ? (category.sessions / totalSessions) * 100 : 0,
    })),
    [categories, totalSessions],
  );

  const sorted = useMemo(() => sortRows(enriched, sort.key, sort.dir), [enriched, sort]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No referral data for this period." padding="py-12" />;
  }

  const proportionSegments = [...sorted].sort((segmentA, segmentB) => segmentB.sessions - segmentA.sessions);

  return (
    <div>
      {totalSessions > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Traffic share</span>
            <span className="text-[11px] text-gray-400 dark:text-gray-500">{fmtNumber(totalSessions)} sessions</span>
          </div>
          <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
            {proportionSegments.map((segment) => (
              <div
                key={segment.category}
                title={`${segment.category}: ${fmtNumber(segment.sessions)} (${segment.pct_of_total.toFixed(1)}%)`}
                style={{ width: `${segment.pct_of_total}%`, backgroundColor: REFERRAL_CATEGORY_COLORS[segment.category] || '#6b7280' }}
              />
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {proportionSegments.map((segment) => (
              <div key={segment.category} className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-400">
                <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: REFERRAL_CATEGORY_COLORS[segment.category] || '#6b7280' }} />
                <span>{segment.category}</span>
                <span className="text-gray-400 dark:text-gray-500">{segment.pct_of_total.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
        <table className="responsive-card-table w-full text-sm table-fixed min-w-[820px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader label="Category" sortKey="category" currentSort={sort} onSort={handleSort} className="w-[20%]" />
              <SortableHeader label="Sources" sortKey="count" currentSort={sort} onSort={handleSort} className="w-[36%]" />
              <SortableHeader label="Sessions" sortKey="sessions" currentSort={sort} onSort={handleSort} className="w-[11%]" />
              <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} className="w-[11%]" />
              <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} className="w-[11%]" />
              <SortableHeader label="% of Total" sortKey="pct_of_total" currentSort={sort} onSort={handleSort} className="w-[11%]" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <React.Fragment key={row.category}>
                <tr
                  {...expandableRowProps({
                    expanded: expandedCategory === row.category,
                    onToggle: () => {
                      setExpandedCategory((prev) => prev === row.category ? null : row.category);
                      setDetailPage((prev) => ({ ...prev, [row.category]: 1 }));
                    },
                    label: `sources in ${row.category}`,
                    className: `border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition cursor-pointer ${expandedCategory === row.category ? '' : 'rc-collapsed'}`,
                  })}
                >
                  <td data-label="Category" className={`rc-title-cell px-3 py-2.5 text-gray-900 dark:text-gray-100 font-medium ${expandedCategory === row.category ? 'rc-title-open' : ''}`}>
                    <span className="inline-flex items-center gap-1.5">
                      <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expandedCategory === row.category ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                      {row.category}
                    </span>
                  </td>
                  <td data-label="Sources" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.count)}</td>
                  <td data-label="Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.sessions)}</td>
                  <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.users)}</td>
                  <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.conversions)}</td>
                  <td data-label="% of Total" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{row.pct_of_total.toFixed(1)}%</td>
                </tr>
                {expandedCategory === row.category && (() => {
                  const allDetails = (categoryDetails[row.category] || []).sort((detailA, detailB) => (detailB.sessions || 0) - (detailA.sessions || 0));
                  const currentPage = detailPage[row.category] || 1;
                  const totalItems = allDetails.length;
                  const totalPages = Math.ceil(totalItems / detailPerPage);
                  const paged = allDetails.slice((currentPage - 1) * detailPerPage, currentPage * detailPerPage);
                  return (
                    <>
                      {paged.map((detail, index) => (
                        <tr key={`${detail.source}-${index}`} className="rc-cells-top border-b dark:border-gray-800/50 bg-gray-50/50 dark:bg-gray-800/30">
                          <td data-label="Source" className="px-3 py-2 align-top text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                            <span className="pl-6 max-[499px]:pl-0">{detail.source}</span>
                          </td>
                          <td data-label="URL" className="px-3 py-2 align-top text-xs text-gray-500 dark:text-gray-500 truncate max-w-0 max-[499px]:max-w-none max-[499px]:whitespace-normal max-[499px]:break-all" title={detail.referrer_url || ''}>{detail.referrer_url || '—'}</td>
                          <td data-label="Sessions" className="px-3 py-2 align-top text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(detail.sessions)}</td>
                          <td data-label="Users" className="px-3 py-2 align-top text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(detail.users)}</td>
                          <td data-label="Conversions" className="px-3 py-2 align-top text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(detail.conversions)}</td>
                          <td className="rc-hide px-3 py-2" aria-hidden="true" />
                        </tr>
                      ))}
                      {totalPages > 1 && (
                        <tr className="bg-gray-50/50 dark:bg-gray-800/30">
                          <td colSpan={6} className="px-3 py-2">
                            <PaginationFooter
                              pagination={{ total_items: totalItems, total_pages: totalPages }}
                              page={currentPage}
                              perPage={detailPerPage}
                              onPageChange={(nextPage) => setDetailPage((prev) => ({ ...prev, [row.category]: nextPage }))}
                              label="sources"
                            />
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })()}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </ThinScroll>
    </div>
  );
};

/* ============================= Insight summary ============================= */
export const getReferralsSummary = (data) => {
  if (!data || data.length === 0) return { summary: [], warnings: [] };
  const grouped = {};
  let total = 0;
  for (const row of data) {
    const category = classifyReferral(row.source);
    const sessions = row.sessions || 0;
    grouped[category] = (grouped[category] || 0) + sessions;
    total += sessions;
  }
  const summary = [];
  const sorted = Object.entries(grouped).sort((entryA, entryB) => entryB[1] - entryA[1]);
  if (sorted.length >= 3 && total > 0) {
    const top3 = sorted.slice(0, 3);
    const top3Pct = ((top3.reduce((sum, [, sessions]) => sum + sessions, 0) / total) * 100).toFixed(1);
    summary.push(`Top 3 categories (${top3.map(([category]) => category).join(', ')}) account for ${top3Pct}% of referral traffic.`);
  } else if (sorted.length > 0 && total > 0) {
    const [topCat, topVal] = sorted[0];
    const pct = ((topVal / total) * 100).toFixed(1);
    summary.push(`Top category is ${topCat} with ${pct}% of referral traffic.`);
  }
  return { summary, warnings: [] };
};

/* ============================= Export helpers ============================= */
// One column spec drives CSV + PDF (can't drift). CSV emits raw values; PDF emits
// formatted counts + abbreviated "Conv." header.
const REFERRALS_EXPORT = makeExport([
  { header: 'Source', get: (row) => row.source },
  { header: 'Referrer URL', get: (row) => row.referrer_url },
  { header: 'Sessions', get: (row) => row.sessions, pdf: (row) => fmtNumber(row.sessions) },
  { header: 'Users', get: (row) => row.users, pdf: (row) => fmtNumber(row.users) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (row) => row.conversions, pdf: (row) => fmtNumber(row.conversions) },
]);

export const exportReferralsCsv = (referralsData, rangeStart, rangeEnd) => {
  if (!referralsData?.length) return;
  downloadCsv(REFERRALS_EXPORT.headers, referralsData.map(REFERRALS_EXPORT.toCsvRow), `ga4-referrals-${rangeStart}-to-${rangeEnd}.csv`);
};

export const exportReferralsPdf = (referralsData, rangeStart, rangeEnd) => {
  if (!referralsData?.length) return;
  const dateRange = rangeStart && rangeEnd ? `${rangeStart} to ${rangeEnd}` : '';
  downloadPdf('Referrals', REFERRALS_EXPORT.pdfHeaders, referralsData.map(REFERRALS_EXPORT.toPdfRow), `ga4-referrals-${rangeStart}-to-${rangeEnd}.pdf`, dateRange);
};

/* ============================= Tab component ============================= */
const ReferralsTab = ({ referralsData, referralsLoading, referralsError }) => {
  const [subTab, setSubTab] = useState('referrals_category');

  const subTabs = [
    { id: 'referrals_category', label: 'By Category' },
    { id: 'referrals', label: 'All Referrers' },
  ];

  return (
    <div>
      <SectionInsightCard title="Referrals Insight" {...getReferralsSummary(referralsData)} />

      <div className="flex items-center gap-1.5 mb-4">
        {subTabs.map((subTabItem) => (
          <button
            key={subTabItem.id}
            type="button"
            onClick={() => setSubTab(subTabItem.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
              subTab === subTabItem.id
                ? 'bg-[#32B1FF]/10 text-[#32B1FF] border border-[#32B1FF]/30'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
            }`}
          >
            {subTabItem.label}
          </button>
        ))}
      </div>

      {subTab === 'referrals' && (
        <ReferralsTable data={referralsData} loading={referralsLoading} error={referralsError} />
      )}
      {subTab === 'referrals_category' && (
        <ReferralsByCategoryTable data={referralsData} loading={referralsLoading} error={referralsError} />
      )}
    </div>
  );
};

export default ReferralsTab;
